import { Box } from '@mui/material';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { Image3D } from '~/domain/patient/image3d';
import { useIsInSessionRoute } from '~/hooks/session/useIsInSessionRoute';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import ImageActionMenu from './ImageActionMenu';
import ImageDetails from './ImageDetails';
import ImagePicker from './ImagePicker/PrimaryImagePicker';
import ImageTitle from './ImageTitle';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';

type PrimaryImageProps = {
    image: Image3D;
    availableImages: AvailableImage[];
    onChange: (image: AvailableImage) => void;
};

export default function PrimaryImage({
    image,
    availableImages,
    onChange,
}: PrimaryImageProps) {
    const [isExpanded, setIsExpanded] = useState(true);
    const inSessionRoute = useIsInSessionRoute();
    const setSelectedPatientIdState = useSetRecoilState(selectedPatientIdState);
    const { removePrimaryImage } = useViewportEnvironment();

    const onUnloadPrimaryImage = () => {
        setSelectedPatientIdState(null);
        removePrimaryImage();
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <Box px={2}>
            <Box
                display="flex"
                position="relative"
                justifyContent="space-between"
            >
                <Box
                    display="flex"
                    gap={0.5}
                    alignItems="center"
                    onClick={toggleExpand}
                    sx={(theme) => ({
                        cursor: 'pointer',
                        color: theme.palette.primary.main,
                    })}
                >
                    <Box display="flex" alignItems="center">
                        {isExpanded ? (
                            <ChevronDown size={14} />
                        ) : (
                            <ChevronUp size={14} />
                        )}
                    </Box>
                    <Box display="flex" alignItems="center">
                        <ImageTitle
                            label={<FormattedMessage id="primaryImage" />}
                            image={image}
                        />
                    </Box>
                </Box>
                <Box display="flex" gap={0.5} alignItems="center">
                    {availableImages.length > 1 && (
                        <ImagePicker
                            currentPrimaryImage={image}
                            availableImages={availableImages}
                            onChange={onChange}
                        />
                    )}
                    {!inSessionRoute && (
                        <ImageActionMenu
                            image={image}
                            onUnload={onUnloadPrimaryImage}
                        />
                    )}
                </Box>
            </Box>
            {isExpanded && <ImageDetails image={image} />}
        </Box>
    );
}
